import { StyledPageWrapper } from 'components/fragments/PageWrapper/styled';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useAppSelector } from 'hooks/hooks';
import { createRoleAccessChecker } from 'utils/userRolesUtils/userRolesUtils';
import ToolSidebar from 'components/fragments/ToolSidebar/ToolSidebar';
import { useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { DateRange } from 'react-day-picker';
import { FetchElasticSearchLogsRequest } from 'models/apiModels';
import { usePostFetchElasticSearchLogsRequestMutation } from 'store/api/tools-v1';
import { browserDownloadFile } from 'utils/downloadUtils';
import { MdDownloadForOffline } from 'react-icons/md';
import { roles } from '../../constants';
import { DownloadFileButton, StyledFetchElasticSearchLogsPresentation } from './styled';
import { FetchElasticSearchLogsForm, FormInput } from './FetchElasticSearchLogsForm';
import { FetchElasticSearchTimeline } from './FetchElasticSearchTimeline';

export const FetchElasticSearchLogsPage = () => {
  const { role } = useAppSelector((state) => state.auth.session);
  const location = useLocation();
  const navigate = useNavigate();

  const [searchParams, setSearchParams] = useSearchParams();
  const paramDeviceId = searchParams.get('deviceId') ?? undefined;
  const [deviceId, setDeviceId] = useState<string | undefined>(paramDeviceId);
  const paramDateRange = searchParams.get('dateRange') ?? undefined;
  const parsedDateRange = paramDateRange ? JSON.parse(paramDateRange) : undefined;
  const [dateRange, setDateRange] = useState<DateRange | undefined>(parsedDateRange);
  const [isDownloaded, setIsDownloaded] = useState(false);

  if (role === roles.Unauthorized || !createRoleAccessChecker(role)(location.pathname)) {
    navigate('/', { replace: true });
  }
  const currentEnvironment = searchParams.get('env') ?? 'qa';

  const [sendElasticSearchRequest, { data, error: queryError, isLoading, isSuccess }] =
    usePostFetchElasticSearchLogsRequestMutation();

  const onSubmit: SubmitHandler<FormInput> = (formData) => {
    const oldParams = Object.fromEntries(searchParams);
    const params = {
      ...oldParams,
      deviceId: formData.deviceId,
    };
    setSearchParams(params, { replace: true });
    setDeviceId(formData.deviceId);
    setDateRange(formData.dateRange);

    const fetchElasticSearchRequest: FetchElasticSearchLogsRequest = {
      deviceId: formData.deviceId,
      startTime: formData.dateRange.from?.toISOString(),
      endTime: formData.dateRange.to?.toISOString(),
      environment: currentEnvironment,
    };

    sendElasticSearchRequest(fetchElasticSearchRequest);
  };

  const onDownloadFile = () => {
    browserDownloadFile(new Blob([data], { type: 'application/json' }), `${deviceId}.json`);
    setIsDownloaded(true);
    setTimeout(() => {
      setIsDownloaded(false);
    }, 1000);
  };

  const {
    register,
    handleSubmit,
    control,
    formState: { errors: formErrors },
  } = useForm<FormInput>({
    defaultValues: {
      deviceId: deviceId ?? '',
      dateRange: dateRange ?? undefined,
    },
  });

  return (
    <>
      <ToolSidebar>
        <FetchElasticSearchLogsForm
          onSubmit={handleSubmit(onSubmit)}
          register={register}
          errors={formErrors}
          control={control}
        />
      </ToolSidebar>
      <StyledPageWrapper>
        <StyledFetchElasticSearchLogsPresentation>
          <h1> Fetch Elastic Search Logs for a specific mower</h1>
          {isLoading && <h4>Loading...</h4>}
          {queryError && !isSuccess && <h2> Failed to fetch Elastic Search Logs</h2>}
          {!queryError && isSuccess && data === '[]' && (
            <h2> No logs for this mower in this specific time period</h2>
          )}
          {!queryError && isSuccess && data && data !== '[]' && (
            <>
              <h3>Fetched Logs</h3>
              <p>
                The first fifteen logs are shown here, if you want to see more, click the download
                button.{' '}
              </p>
              <DownloadFileButton onClick={onDownloadFile}>
                {isDownloaded ? (
                  <MdDownloadForOffline size={30} color="green" />
                ) : (
                  <MdDownloadForOffline size={30} />
                )}
              </DownloadFileButton>
              <FetchElasticSearchTimeline queryData={data} />
            </>
          )}
          <h4> Instructions: </h4>
          <p>
            {' '}
            Use this tool to check whether a specific lawn mower has been disconnected or connected
            during a certain time period.
          </p>
          <p>
            To use this tool, choose the environment in the page header to which the mower is
            currently pointing.{' '}
          </p>
          <p>
            Enter the Device Id of the mower for which you want to fetch Elastic Search Logs. The
            device ID for a G3 mower is the nine-digit serial number of the mower,
          </p>
          <p>
            followed by a dash, followed by the nine-digit comboard ID. For G4 mowers, just input
            the nine-digit serial number of the mower.
          </p>
          <p>Enter the start and end dates in the calendar for the period you want to search.</p>
        </StyledFetchElasticSearchLogsPresentation>
      </StyledPageWrapper>
      ;
    </>
  );
};
